import React, { useRef, useState } from 'react';
import Draggable, { DraggableData, DraggableEvent } from 'react-draggable';
import { Box, Button, SpeedDial, SpeedDialAction } from '@mui/material';
import { alpha } from '@mui/material/styles';
import ForumIcon from '@mui/icons-material/Forum';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../../redux/store';
import AnaAvatar from '../../../pages/Ana/AnaAvatar';

const FAB_SIZE = 56;
/** Movement (px) beyond which a pointer interaction counts as a drag rather than a click. */
const DRAG_THRESHOLD = 5;

interface ChatbotProps {
	handleOpenAiAssistant: () => void;
	setAIAgentsModalOpen: (open: boolean) => void;
	hidden?: boolean;
}

/** "Modern" style: a single Ana avatar speed dial that fans out its actions. */
const ModernChatbot = ({ handleOpenAiAssistant, setAIAgentsModalOpen }: ChatbotProps) => {
	const { t } = useTranslation();

	return (
		<SpeedDial
			ariaLabel={t('ana.fab.ariaLabel', 'Ana assistant')}
			direction="up"
			icon={<AnaAvatar size={FAB_SIZE} animated />}
			sx={{
				position: 'relative',
				'& .MuiSpeedDial-fab': {
					width: FAB_SIZE,
					height: FAB_SIZE,
					p: 0,
					minHeight: 'unset',
					backgroundColor: 'transparent',
					boxShadow: (theme) => `0 6px 20px ${alpha(theme.palette.common.black, 0.28)}`,
					overflow: 'hidden',
					transition: 'transform 0.2s ease, box-shadow 0.2s ease',
					'&:hover': {
						backgroundColor: 'transparent',
						transform: 'scale(1.06)',
						boxShadow: (theme) => `0 8px 26px ${alpha(theme.palette.common.black, 0.36)}`
					},
					'&:focus-visible': {
						outline: (theme) => `2px solid ${theme.palette.primary.main}`,
						outlineOffset: 3
					}
				}
			}}
		>
			<SpeedDialAction
				icon={<ForumIcon />}
				tooltipTitle={t('ana.fab.askAna', 'Ask')}
				tooltipOpen
				onClick={handleOpenAiAssistant}
			/>
			<SpeedDialAction
				icon={<AutoAwesomeIcon />}
				tooltipTitle={t('ana.fab.aiAgents', 'Agents')}
				tooltipOpen
				onClick={() => setAIAgentsModalOpen(true)}
			/>
		</SpeedDial>
	);
};

/** "Classic" style: the original two-button bar. */
const ClassicChatbot = ({ handleOpenAiAssistant, setAIAgentsModalOpen }: ChatbotProps) => {
	const { t } = useTranslation();

	return (
		<Box sx={{ display: 'flex', gap: '0.5rem' }}>
			<Button
				variant="contained"
				sx={{ width: 50, height: 50, minWidth: 0 }}
				onClick={handleOpenAiAssistant}
				aria-label={t('ana.fab.askAna', 'Ask')}
			>
				<ForumIcon sx={{ fontSize: '2rem' }} />
			</Button>
			<Button
				sx={{ width: 50, height: 50, minWidth: 0 }}
				onClick={() => setAIAgentsModalOpen(true)}
				aria-label={t('ana.fab.aiAgents', 'Agents')}
			>
				<MoreVertIcon sx={{ fontSize: '2rem' }} />
			</Button>
		</Box>
	);
};

const DraggableChatbot = (props: ChatbotProps) => {
	const { hidden = false } = props;
	const fabStyle = useAppSelector((state) => state.platformSettings.anaFabStyle);
	const nodeRef = useRef<HTMLDivElement>(null);
	const dragStart = useRef({ x: 0, y: 0 });
	const moved = useRef(false);
	const [position, setPosition] = useState({ x: 0, y: 0 });

	const handleStart = (_event: DraggableEvent, data: DraggableData) => {
		dragStart.current = { x: data.x, y: data.y };
		moved.current = false;
	};

	const handleDrag = (_event: DraggableEvent, data: DraggableData) => {
		setPosition({ x: data.x, y: data.y });
		if (
			Math.abs(data.x - dragStart.current.x) > DRAG_THRESHOLD ||
			Math.abs(data.y - dragStart.current.y) > DRAG_THRESHOLD
		) {
			moved.current = true;
		}
	};

	// Swallow the click that fires at the end of a drag so the FAB doesn't also activate.
	const handleClickCapture = (event: React.MouseEvent) => {
		if (moved.current) {
			event.preventDefault();
			event.stopPropagation();
			moved.current = false;
		}
	};

	return (
		<Draggable nodeRef={nodeRef} position={position} allowMobileScroll onStart={handleStart} onDrag={handleDrag}>
			<Box
				ref={nodeRef}
				onClickCapture={handleClickCapture}
				sx={{
					position: 'fixed',
					bottom: 100,
					right: 20,
					zIndex: 1300,
					cursor: 'grab',
					touchAction: 'none',
					'&:active': { cursor: 'grabbing' },
					opacity: hidden ? 0 : 1,
					pointerEvents: hidden ? 'none' : 'auto',
					transition: 'opacity 0.2s ease'
				}}
			>
				{fabStyle === 'classic' ? <ClassicChatbot {...props} /> : <ModernChatbot {...props} />}
			</Box>
		</Draggable>
	);
};

export default DraggableChatbot;
